import React, { useEffect, useState } from "react";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { TID } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { ShieldCheck, X, ShieldStar, User, Download, Trash, UserPlus, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);

  const reload = async () => { const { data } = await api.get("/users"); setUsers(data || []); };
  useEffect(() => { reload().catch(() => {}); }, []);

  const approve = async (id) => { await api.post(`/users/${id}/approve`); toast.success("User approved"); reload(); };
  const disable = async (id) => { if (!window.confirm("Disable this user?")) return; await api.post(`/users/${id}/disable`); toast.success("User disabled"); reload(); };
  const removeUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name || u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // Add-user dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState({ name: "", email: "", phone: "", password: "", role: "employee" });
  const [creating, setCreating] = useState(false);
  const createUser = async () => {
    if (!draft.name.trim() || !draft.email.trim() || draft.password.length < 8) {
      return toast.error("Name, email and password (8+ chars) required");
    }
    setCreating(true);
    try {
      await api.post("/users", {
        name: draft.name.trim(),
        email: draft.email.trim(),
        phone: draft.phone.trim() || null,
        password: draft.password,
      }, { params: { role: draft.role } });
      toast.success(`Created ${draft.email}`);
      setAddOpen(false);
      setDraft({ name: "", email: "", phone: "", password: "", role: "employee" });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };
  const setRole = async (id, role) => { await api.post(`/users/${id}/role`, null, { params: { role } }); toast.success(`Role: ${role}`); reload(); };

  const statusBadge = (s) => {
    const map = { active: "bg-emerald-50 text-emerald-700 border-emerald-200", pending: "bg-amber-50 text-amber-800 border-amber-200", disabled: "bg-slate-100 text-slate-600 border-slate-200" };
    return <span className={`text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-0.5 rounded border ${map[s] || map.active}`}>{s}</span>;
  };

  const exportUsers = async () => {
    try {
      const csv = toUsersCsv(users);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `users-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${users.length} users to CSV`);
    } catch { toast.error("Export failed"); }
  };

  // Bulk import state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCsv, setBulkCsv] = useState("");
  const [bulkDefaultPw, setBulkDefaultPw] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const onPickCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setBulkCsv(text);
    setBulkResult(null);
  };
  const runBulkImport = async () => {
    if (!bulkCsv.trim()) return toast.error("Paste or upload a CSV first");
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const { data } = await api.post("/users/bulk-import", {
        csv: bulkCsv,
        default_password: bulkDefaultPw.trim() || null,
      });
      setBulkResult(data);
      toast.success(`Imported ${data.totals.created} user${data.totals.created === 1 ? "" : "s"}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Admin</div>
          <h1 className="font-heading text-4xl font-bold tracking-tighter">User management</h1>
          <p className="text-slate-600 mt-1">Approve new signups, change roles, and disable accounts.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-user-button">
                <UserPlus size={14} className="mr-1.5" /> Add user
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add a new user</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Full name *</label>
                  <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="new-user-name" placeholder="Jane Doe" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Email *</label>
                  <Input type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} data-testid="new-user-email" placeholder="jane@choicecapitalsolutions.com" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Phone (optional)</label>
                  <Input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} data-testid="new-user-phone" placeholder="+1 555-…" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Temp password * (min 8 chars)</label>
                  <Input type="text" value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} data-testid="new-user-password" placeholder="Share this with them — they can change it later" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Role</label>
                  <Select value={draft.role} onValueChange={(v) => setDraft({ ...draft, role: v })}>
                    <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="leader">Leader (no approval needed)</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-[11px] text-slate-500 italic">New users are created already active — no signup approval needed.</p>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
                <Button onClick={createUser} disabled={creating} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-user-submit">
                  {creating ? "Creating…" : "Create user"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button onClick={exportUsers} variant="outline" data-testid="export-users-button">
            <Download size={14} className="mr-1.5" /> Export users (CSV)
          </Button>
          <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkResult(null); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="bulk-import-button">
                <UploadSimple size={14} className="mr-1.5" /> Bulk import (CSV)
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>Bulk-import users from CSV</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <p className="text-xs text-slate-600">
                  Columns expected (case-insensitive): <span className="font-mono">Name, Email, Role, Status, Password</span>.
                  Missing role/status default to <span className="font-mono">employee / active</span>. Existing emails are skipped.
                </p>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Upload .csv file</label>
                  <input type="file" accept=".csv,text/csv" onChange={onPickCsvFile} data-testid="bulk-csv-file" className="block mt-1 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">…or paste CSV content</label>
                  <textarea
                    value={bulkCsv}
                    onChange={(e) => { setBulkCsv(e.target.value); setBulkResult(null); }}
                    rows={6}
                    placeholder="Name,Email,Role,Status,Password&#10;Jane Doe,jane@choicecapitalsolutions.com,Employee,Active,Choice@1!"
                    className="w-full mt-1 px-3 py-2 text-xs font-mono border border-slate-300 rounded-md focus:border-slate-900 focus:ring-2 focus:ring-slate-200 outline-none"
                    data-testid="bulk-csv-textarea"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Default password (used if a row&apos;s Password is blank)</label>
                  <Input value={bulkDefaultPw} onChange={(e) => setBulkDefaultPw(e.target.value)} placeholder="optional" data-testid="bulk-default-password" />
                </div>
                {bulkResult && (
                  <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs space-y-1.5" data-testid="bulk-result">
                    <div className="font-semibold text-emerald-700">✓ Created: {bulkResult.totals.created}</div>
                    {bulkResult.totals.skipped_existing > 0 && (
                      <div className="text-slate-600">Skipped (already existed): {bulkResult.totals.skipped_existing}</div>
                    )}
                    {bulkResult.totals.duplicates_in_csv > 0 && (
                      <div className="text-amber-700">Duplicate rows inside CSV (kept first): {bulkResult.totals.duplicates_in_csv}</div>
                    )}
                    {bulkResult.totals.errors > 0 && (
                      <div className="text-red-700">
                        <div>Errors: {bulkResult.totals.errors}</div>
                        <ul className="mt-1 pl-4 list-disc space-y-0.5">
                          {bulkResult.errors.slice(0, 8).map((er, i) => (
                            <li key={i}>row {er.row} <span className="font-mono">{er.email || "—"}</span>: {er.reason}</li>
                          ))}
                          {bulkResult.errors.length > 8 && <li>… +{bulkResult.errors.length - 8} more</li>}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setBulkOpen(false)}>Close</Button>
                <Button onClick={runBulkImport} disabled={bulkBusy || !bulkCsv.trim()} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="bulk-import-submit">
                  {bulkBusy ? "Importing…" : "Import"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.15em] text-slate-500">
            <tr>
              <th className="text-left px-6 py-3 font-semibold">User</th>
              <th className="text-left px-6 py-3 font-semibold">Email</th>
              <th className="text-left px-6 py-3 font-semibold">Role</th>
              <th className="text-left px-6 py-3 font-semibold">Status</th>
              <th className="text-left px-6 py-3 font-semibold">Signed up</th>
              <th className="text-right px-6 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-200 hover:bg-slate-50">
                <td className="px-6 py-3 font-semibold flex items-center gap-2">
                  {u.role === "admin" ? <ShieldStar size={14} className="text-blue-600" weight="fill" /> : u.role === "leader" ? <ShieldStar size={14} className="text-amber-500" weight="fill" /> : <User size={14} className="text-slate-400" />}
                  {u.name}
                </td>
                <td className="px-6 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-6 py-3">
                  <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                    <SelectTrigger data-testid={TID.roleSelect(u.id)} className="w-32 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="leader">Leader</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-6 py-3">{statusBadge(u.status)}</td>
                <td className="px-6 py-3 text-xs text-slate-500">{new Date(u.created_at).toLocaleString()}</td>
                <td className="px-6 py-3 text-right space-x-2">
                  {u.status === "pending" && (
                    <Button size="sm" onClick={() => approve(u.id)} data-testid={TID.approveUser(u.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      <ShieldCheck size={13} className="mr-1" /> Approve
                    </Button>
                  )}
                  {u.status !== "disabled" && (
                    <Button size="sm" variant="outline" onClick={() => disable(u.id)} data-testid={TID.disableUser(u.id)} className="text-red-600 hover:bg-red-50 border-red-200">
                      <X size={13} className="mr-1" /> Disable
                    </Button>
                  )}
                  {u.id !== me?.id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removeUser(u)}
                      data-testid={`delete-user-${u.id}`}
                      className="text-red-700 hover:bg-red-100 hover:text-red-800 border-red-300"
                      title="Permanently delete this user"
                    >
                      <Trash size={13} className="mr-1" /> Delete
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan="7" className="px-6 py-10 text-center text-slate-500">No users yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function toUsersCsv(rows) {
  if (!rows || !rows.length) return "";
  const cols = ["name","email","role","status","can_see_commission","phone","created_at"];
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = cols.join(",");
  const lines = rows.map((r) => cols.map((c) => esc(r[c])).join(","));
  return [header, ...lines].join("\n");
}


